import * as Sentry from '@sentry/react';
import { readLogyxError } from './errors';
import { REQUEST_ID_HEADER } from './requestId';

// One Sentry event when an order call ends without telling whether the order
// went through, and the user is told it may have been sent: no answer that says
// what happened (isAnswerLost), or Finish order's 404 whose cart is gone or
// cannot be read back. Its request id leads to Logyx core's access lines and to
// the order, which stores it as its send's `client_request_id` (requestId.ts).
//
// It carries the call's route template, the HTTP status, the request id and
// the order's or the cart's id. Never the request's body, the answer, or a URL
// (a query can hold a search or a token). Its callers send nothing for a
// business answer (a 409, a 400).
//
// The message names the request id: Sentry's Dedupe drops an event whose
// message and fingerprint equal the previous event's, so two lost answers in a
// row would send one. The fingerprint keeps one issue per call.

export const LostAnswerEndpoint = {
  CART_ORDER: 'POST /v1/carts/order',
  RESEND: 'POST /v1/orders/:id/resend',
} as const;

type LostAnswerEndpointType =
  typeof LostAnswerEndpoint[keyof typeof LostAnswerEndpoint];

interface ILostAnswerContext {
  endpoint: LostAnswerEndpointType;
  orderId?: number | string;
  cartId?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

// A header's value, whatever the case of its name: axios keeps a request's as
// the interceptor wrote it, and gives an answer's in lowercase.
const readHeader = (headers: unknown, name: string): string | undefined => {
  if (!isRecord(headers)) {
    return undefined;
  }
  const key = Object.keys(headers).find(
    (header) => header.toLowerCase() === name.toLowerCase()
  );
  const value = key === undefined ? undefined : headers[key];
  return typeof value === 'string' && value ? value : undefined;
};

// The id that was sent. The answer's echo is read only when the request's
// headers are missing: a proxy's 5xx has no CORS headers, so no echo, and
// Logyx core echoes Forms' id unchanged.
const readRequestId = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }
  const sent = isRecord(error.config)
    ? readHeader(error.config.headers, REQUEST_ID_HEADER)
    : undefined;
  const echoed = isRecord(error.response)
    ? readHeader(error.response.headers, REQUEST_ID_HEADER)
    : undefined;
  return sent ?? echoed;
};

// What captureLostAnswer sends: the message and Sentry's capture context.
const lostAnswerEvent = (error: unknown, context: ILostAnswerContext) => {
  const requestId = readRequestId(error);
  const status = readLogyxError(error).status;
  const orderId =
    context.orderId === undefined || context.orderId === null
      ? ''
      : String(context.orderId);
  const tags: Record<string, string> = {
    'logyx.endpoint': context.endpoint,
    'logyx.status': status === undefined ? 'none' : String(status),
    request_id: requestId ?? 'none',
  };
  if (orderId) {
    tags['logyx.order_id'] = orderId;
  }
  if (typeof context.cartId === 'string' && context.cartId) {
    tags['logyx.cart_id'] = context.cartId;
  }
  return {
    message: `Logyx order call: no answer (${requestId ?? 'no request id'})`,
    captureContext: {
      level: 'error' as const,
      fingerprint: ['logyx-lost-answer', context.endpoint],
      tags,
    },
  };
};

// Never throws: its callers handle a failed request, and react-query 3 never
// ends a mutation whose onError throws (errors.ts, readLogyxError).
export const captureLostAnswer = (
  error: unknown,
  context: ILostAnswerContext
): void => {
  try {
    const { message, captureContext } = lostAnswerEvent(error, context);
    Sentry.captureMessage(message, captureContext);
  } catch {
    // Nothing to report with: the user's dialog or toast still shows.
  }
};
